/**
 * 任务耗时预估与完成提醒（网页版）
 *
 * ⚠️ 与小程序端 `ceanx-mini/src/utils/task.js` **保持同一套口径**：
 *    两端的预估算法、文案措辞必须一致，否则同一个任务在两端显示不同的
 *    预期时间，用户会觉得产品不专业。改这里时请同步改那边。
 */

/* ---------------- 耗时预估 ---------------- */

/** 秒 → 人话。刻意说「约」：这是经验值，说太细反而显得不准。 */
export function formatEta(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  if (s < 10) return '预计还需几秒';
  if (s < 60) return `预计还需约 ${Math.round(s / 5) * 5} 秒`;
  const m = s / 60;
  if (m < 1.5) return '预计还需约 1 分钟';
  if (m < 10) return `预计还需约 ${Math.round(m)} 分钟`;
  return `预计还需约 ${Math.round(m / 5) * 5} 分钟`;
}

/**
 * 普通转换任务的静态预估（进度只有 10 → 100 两个点，推算不出速率）。
 * 宁可比实际长一点 —— 用户等超了预期会焦虑，提前完成则无感。
 */
export function estimateConvertSeconds(sizeBytes) {
  const mb = (Number(sizeBytes) || 0) / 1048576;
  if (mb <= 0.5) return 5;
  if (mb <= 2) return 10;
  if (mb <= 5) return 20;
  if (mb <= 20) return 45;
  if (mb <= 50) return 90;
  return 150;
}

/**
 * OCR 识别的静态预估（提交后前端拿不到页数，只能按文件体量粗估）。
 * 依据实测：版式 0.7~2 秒/页，简单页约 5~12 秒/页、表格密集页 40~52 秒/页，
 * 页级并发 3；200dpi 扫描页约 200~500KB。按「混合文档」取中位并刻意放长。
 */
export function estimateOcrSeconds(sizeBytes) {
  const mb = (Number(sizeBytes) || 0) / 1048576;
  if (mb <= 0.5) return 30;
  if (mb <= 2) return 60;
  if (mb <= 5) return 150;
  if (mb <= 10) return 300;
  if (mb <= 30) return 600;
  return 900;
}

/**
 * 剩余时间文案：**只信后端下发的实测值**。
 *
 * @param {number|null|undefined} etaSeconds 后端 `etaSeconds`
 *        （= 实测每页耗时 × 剩余页数，见 ceanx/tasks/worker.py::_PageMeter）
 * @returns {string} 估不出来返回空串 → 模板整行不渲染
 */
export function etaText(etaSeconds) {
  const s = Number(etaSeconds);
  if (etaSeconds == null || !isFinite(s) || s <= 0) return '';
  return formatEta(s);
}

/**
 * 任务卡上那行状态文字：优先给**真实页刻度**，再给时间。
 *
 * ⛔ 2026-10-24：删除旧实现 `etaSecondsFromProgress(progress, startedAt)`。
 *    它用 `elapsed × (100-p) / p` 在渲染期现算，进度一旦停住（智能分流路径整份
 *    文档都停在 5%），分母越来越大 → 预估只增不减，且没有上限。
 *    「用假数字填补等待焦虑」比什么都不显示更伤信任，故一并删掉，不留复用入口。
 *
 * @param {object} row 任务行（含 pagesDone / pagesTotal / etaSeconds / status）
 * @returns {string} 例：「已识别 3/12 页 · 预计还需约 2 分钟」；无可显示内容返回空串
 */
export function progressLine(row) {
  if (!row || row.status !== 'running') return '';
  const done = Math.max(0, Number(row.pagesDone) || 0);
  const total = Math.max(0, Number(row.pagesTotal) || 0);
  const pages = total > 0 ? `已识别 ${done}/${total} 页` : '';
  // 页已全部识别 → 后端在合并/写文件，这一段没有可测速率，不编时间
  const eta = (total > 0 && done >= total)
    ? '正在整理文档…'
    : etaText(row.etaSeconds);
  if (pages && eta) return `${pages} · ${eta}`;
  if (pages || eta) return pages || eta;
  // 还没测到速率（首屏光栅化 / 单张图片）：OCR 给一句不定态，别留空行像卡住
  return row.kind === 'ocr' ? '正在识别…' : '';
}

/* ---------------- 完成提醒 ----------------
 * ⚠️ 网页版的先天限制（要对用户诚实）：
 *   * 桌面通知只在**页面还开着**时有效 —— 关掉标签页就收不到，那需要
 *     Service Worker + 推送服务器，成本远高于收益；
 *   * 所以真正兜底的是「任务中心」列表 + 页面标题变化。
 *   小程序那边有微信订阅消息，能力比这里强得多。
 */

export function canNotify() {
  return typeof window !== 'undefined' && typeof Notification !== 'undefined';
}

/** 已是 granted 返回 true；default 时才弹授权（denied 不再骚扰用户）。 */
export function ensureNotifyPermission() {
  if (!canNotify()) return Promise.resolve(false);
  if (Notification.permission === 'granted') return Promise.resolve(true);
  if (Notification.permission === 'denied') return Promise.resolve(false);
  try {
    // Safari 老版本只支持回调形式，这里兼容两种
    const r = Notification.requestPermission();
    if (r && typeof r.then === 'function') return r.then((p) => p === 'granted');
    return Promise.resolve(false);
  } catch (e) {
    return Promise.resolve(false);
  }
}

/**
 * 发桌面通知。
 *
 * 两个不打扰原则：未授权不发；**页面正处于前台时不发**（人就在看着，
 * 再弹一个系统通知只会烦人，页面内的 message 提示已经够了）。
 */
export function notifyTaskDone(title, body) {
  try {
    if (!canNotify() || Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && !document.hidden) return;
    const n = new Notification(title, { body: body || '', tag: 'ceanx-task' });
    setTimeout(() => { try { n.close(); } catch (e) { /* 忽略 */ } }, 8000);
    n.onclick = () => { try { window.focus(); n.close(); } catch (e) { /* 忽略 */ } };
  } catch (e) {
    /* 通知失败绝不影响任务主流程 */
  }
}

/* ---------------- 页面标题提示 ---------------- */
let _baseTitle = '';

/**
 * 在标题前加状态前缀（如「处理中 · 纯净未知」），切到别的浏览器标签也能
 * 一眼看到任务进度 —— 这是**唯一不需要任何授权**的提醒方式。
 * 传空串恢复原标题。
 */
export function titleHint(text) {
  try {
    if (typeof document === 'undefined') return;
    if (!_baseTitle) _baseTitle = document.title;
    document.title = text ? `${text} · ${_baseTitle}` : _baseTitle;
  } catch (e) {
    /* 忽略 */
  }
}
